import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PendingReport, PendingReportsStats } from '../types';
import { pendingReportsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './PendingReports.css';

const PendingReports: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<PendingReport[]>([]);
  const [stats, setStats] = useState<PendingReportsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    reportType: ''
  });
  const [sortBy, setSortBy] = useState('student_name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const reportTypes = [
    'Client Assessment Report',
    'Client IEP Report',
    'Communication Report',
    'Quarterly Report'
  ];

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        sortBy,
        sortOrder
      };

      if (filters.reportType) {
        params.reportType = filters.reportType;
      }

      const response = await pendingReportsApi.getAll(params);
      setReports(response.data.reports);
    } catch (error) {
      console.error('Error fetching pending reports:', error);
      toast.error('Failed to load pending reports');
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await pendingReportsApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await pendingReportsApi.refresh();
      toast.success('Reports refreshed successfully');
      await fetchReports();
      await fetchStats();
    } catch (error) {
      console.error('Error refreshing reports:', error);
      toast.error('Failed to refresh reports');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async () => {
    try {
      await pendingReportsApi.export();
      const exportType = filters.reportType || 'All Reports';
      toast.success(`${exportType} exported successfully`);
    } catch (error) {
      console.error('Error exporting reports:', error);
      toast.error('Failed to export reports');
    }
  };

  const handleResetReports = async () => {
    const confirmation = window.prompt('Type RESET_PENDING_REPORTS to clear cached pending reports.');
    if (confirmation !== 'RESET_PENDING_REPORTS') {
      if (confirmation !== null) {
        toast.info('Reset cancelled. Confirmation phrase did not match.');
      }
      return;
    }

    try {
      await pendingReportsApi.reset(confirmation);
      toast.success('Pending reports cache cleared.');
      await fetchReports();
      await fetchStats();
    } catch (error) {
      console.error('Error resetting pending reports:', error);
      toast.error('Failed to reset pending reports');
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReportTypeColor = (reportType: string) => {
    switch (reportType) {
      case 'Client Assessment Report':
        return 'report-type-assessment';
      case 'Client IEP Report':
        return 'report-type-iep';
      case 'Communication Report':
        return 'report-type-communication';
      case 'Quarterly Report':
        return 'report-type-quarterly';
      default:
        return '';
    }
  };

  const getDaysOverdueColor = (days: number) => {
    if (days <= 7) return 'days-overdue-low';
    if (days <= 30) return 'days-overdue-medium';
    return 'days-overdue-high';
  };

  return (
    <div className="pending-reports-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Pending Reports</h1>
          <p>Track overdue documents and reports for all students</p>
        </div>
        <div className="header-actions">
          {user?.is_superadmin && (
            <button
              className="btn btn-danger refresh-btn"
              onClick={handleResetReports}
              disabled={refreshing}
              style={{ minWidth: '140px' }}
            >
              🧹 Reset Cache
            </button>
          )}
          <button
            className="btn btn-secondary refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="loading-spinner"></span>
                Refreshing...
              </>
            ) : (
              <>
                <span className="btn-icon">🔄</span>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {stats && stats.data && (
    <div className="stats-section">
        <div className="stats-grid">
            <div className="stat-card total">
                {/* Change is here 👇 */}
                <div className="stat-number">{stats.data.total}</div>
                <div className="stat-label">Total Overdue</div>
            </div>
            {stats.data.byType.map((typeStat) => (
                <div key={typeStat.overdue_report_type} className="stat-card">
                    <div className="stat-number">{typeStat.count}</div>
                    <div className="stat-label">{typeStat.overdue_report_type}</div>
                    <div className="stat-detail">
                        Avg: {Math.round(typeStat.avg_days_overdue)} days overdue
                    </div>
                </div>
            ))}
        </div>
    </div>
)}

      <div className="page-content">
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="type-filter">Report Type:</label>
              <select
                id="type-filter"
                value={filters.reportType}
                onChange={(e) => handleFilterChange('reportType', e.target.value)}
                className="filter-select"
              >
                <option value="">All Report Types</option>
                {reportTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="filters-actions">
              <button
                className="btn btn-secondary clear-filters-btn"
                onClick={() => setFilters({ reportType: '' })}
              >
                Clear Filters
              </button>

              <button
                className="btn btn-primary export-btn"
                onClick={handleExport}
                disabled={reports.length === 0}
              >
                <span className="btn-icon">📥</span>
                Export {filters.reportType ? filters.reportType : 'All Reports'}
              </button>
            </div>
          </div>
        </div>

        <div className="reports-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading pending reports...</p>
            </div>
          ) : (
            <>
              <table className="reports-table">
                <thead>
                  <tr>
                    <th 
                      className={`sortable ${sortBy === 'student_name' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('student_name')}
                    >
                      Student Name
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'joining_date' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('joining_date')}
                    >
                      Joining Date
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'overdue_report_type' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('overdue_report_type')}
                    >
                      Overdue Report Type
                    </th>
                    <th>Missed Period</th>
                    <th 
                      className={`sortable ${sortBy === 'days_overdue' ? `sorted-${sortOrder.toLowerCase()}` : ''}`}
                      onClick={() => handleSort('days_overdue')}
                    >
                      Days Overdue
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="no-data">
                        {filters.reportType ? 
                          'No pending reports found for the selected filter' : 
                          'No pending reports found - all reports are up to date! 🎉'
                        }
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr key={`${report.student_id}-${report.overdue_report_type}-${report.missed_period}`}>
                        <td className="student-name">{report.student_name}</td>
                        <td className="joining-date">{formatDate(report.joining_date)}</td>
                        <td className="report-type">
                          <span className={`report-type-badge ${getReportTypeColor(report.overdue_report_type)}`}>
                            {report.overdue_report_type}
                          </span>
                        </td>
                        <td className="missed-period">{report.missed_period}</td>
                        <td className="days-overdue">
                          <span className={`days-badge ${getDaysOverdueColor(report.days_overdue)}`}>
                            {report.days_overdue} days
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingReports;
